import * as React from 'react'
import ReactDOM from 'react-dom'
import {Lang} from 'yougile-lang'
import {Time, Util} from 'yougile-util'
import {Hub, User, UserAccount, yougileUser} from 'yougile-model'
import {KeyboardHandlers} from '@extensions/keyboard-handlers'
import {Accounts} from '../client/accounts'
import {DepartmentsClient} from '../client/departments-client'
import {DirectMessages} from '../client/direct-messages'
import {SharedBoardsClient} from '../client/shared-boards-client'
import {Sync} from '../client/sync'
import {ChatUtils} from '../extensions/chat-utils'
import {globals} from '../extensions/globals-base'
import {UserProxy} from '../extensions/proxies/user-proxy'
import {UserAvatarV2} from './common/user-avatar-v2/user-avatar-v2'
import {UserName} from './user-name'

interface Props {
  user: User
}

export class UserInfoCard extends React.Component<Props> {
  private static element: HTMLElement = (() => {
    const el = document.createElement('div')
    document.body.appendChild(el)
    return el
  })()
  private static shown = false

  constructor(props: Props) {
    super(props)
  }

  public static show(user: User, x: number, y: number): void {
    if (!user || !!SharedBoardsClient.getId() || UserInfoCard.shown) {
      return
    }
    ReactDOM.render(<UserInfoCard user={user} />, UserInfoCard.element)
    UserInfoCard.shown = true
    KeyboardHandlers.addKeydown(UserInfoCard.escapeHandler)
    if (user) {
      requestAnimationFrame(() => {
        const el: HTMLElement | null =
          UserInfoCard.element.querySelector('.user-avatar-info')

        if (!el) {
          return
        }
        el.style.visibility = 'hidden'
        el.style.display = 'block'
        const height = el.getBoundingClientRect().height
        const viewportHeight = window.innerHeight
        const padding = 10
        const finalY =
          y + height > viewportHeight - padding
            ? viewportHeight - height - padding
            : y
        el.style.top = finalY + 'px'
        el.style.left = x + 'px'
        el.style.visibility = 'visible'
      })
    }
  }

  public static hide(): void {
    if (UserInfoCard.shown) {
      ReactDOM.unmountComponentAtNode(UserInfoCard.element)
      UserInfoCard.shown = false
      KeyboardHandlers.removeKeydown(UserInfoCard.escapeHandler)
    }
  }

  private static escapeHandler = {
    escape: () => {
      UserInfoCard.hide()
      return true
    }
  }

  private openChat = () => {
    ChatUtils.openChat(DirectMessages.requestChatWith(this.props.user.id))
    UserInfoCard.hide()
  }

  public override componentWillUnmount(): void {
    UserInfoCard.hide()
  }

  public override render(): React.ReactNode {
    if (!this.props.user) {
      return null
    }
    const account = Accounts.get(this.props.user.id)
    let infoStatus = Lang.msg.userOnline
    if (account.status === 'offline') {
      infoStatus = account.lastActivity
        ? Lang.msg.userOfflineSeenAt + Time.formatDateTime(account.lastActivity)
        : Lang.msg.userOffline
    } else if (account.status === 'online' && UserAccount.isInDnd(account)) {
      infoStatus = Lang.msg.userDnd + Time.formatDateTime(account.dndUntil)
    }
    const bem = Util.bemFrom('user-avatar-info')

    const box = Sync.getCurrentSandbox()
    const proxy = UserProxy.create(this.props.user)

    const c = box.get(box.companyId) as Hub
    const eSett = c.data.employeeSettings
      ? c.data.employeeSettings[this.props.user.id]
      : undefined
    const position = eSett ? eSett.position : '—'
    const deps =
      DepartmentsClient.listForUser(box, this.props.user.id)
        .map(d => d.title)
        .join(', ') || '—'

    return (
      <div>
        <div className={bem('overlay')} onClick={UserInfoCard.hide} />
        <div
          className={Util.className({
            'user-avatar-info': true,
            me: this.props.user.id === globals.currentUserId
          })}
        >
          <div className={bem('top')}>
            <div className={bem('ava')}>
              <UserAvatarV2 {...proxy} size={70} hideStatus />
            </div>
            <div className={bem('info')}>
              <div className="user-avatar-info-name">
                <UserName user={this.props.user} />
              </div>
              <div className="user-avatar-info-status">{infoStatus}</div>
              <div className={bem('position')}>
                {Lang.msg.companies.position}:{' '}
                <span className={bem('value')}>{position}</span>
              </div>
              <div className={bem('deps')}>
                {Lang.msg.companies.department}:{' '}
                <span className={bem('value')}>{deps}</span>
              </div>
            </div>
          </div>
          {this.props.user.id !== yougileUser.id && (
            <div
              className="user-avatar-info-btn _button"
              onClick={this.openChat}
            >
              {Lang.msg.openDirectChat}
            </div>
          )}
        </div>
      </div>
    )
  }
}
